// KineticLandingPage.jsx
// ─── SCROLL FIX ──────────────────────────────────────────────────────────────
// DataFittingPage.css sets:  html, body { overflow: hidden }
// This means window.scrollY is always 0 and anchor links don't work.
//
// Fix: .kinetic-landing is the scroll container (height:100vh; overflow-y:auto).
//   • Nav receives a scrollContainerRef and listens to that element's scroll.
//   • Anchor nav links use smooth-scroll into the same container.
//   • No changes needed to the router or any parent layout component.
// ─────────────────────────────────────────────────────────────────────────────
//
// ─── ALIGNMENT NOTES (Sagar's requirements, team sync) ───────────────────────
// 1. Standalone product page, not a dashboard redirect on login.
//    Nav CTA + hero CTA both point to /login; nothing here forces the
//    dashboard. Designed to live on its own domain (kinetic.[domain]).
// 2. What it does / value prop / key benefits + images — Hero, Why section
//    (benefits grid), Features grid, How-it-works steps. Every section pairs
//    copy with a visual (chart card, SVG, mock table, browser mockup).
// 3. Staging → production review workflow — process note, not a code change.
// 4. LinkedIn page + short YouTube demo video (no Facebook/Twitter) — Demo
//    section has a real video card (swap the iframe src once the video is
//    live) + a social-links row; footer also carries LinkedIn/YouTube links.
// 5. Web-based vs. downloadable differentiator (per kintekexplorer.com-style
//    reference) — differentiator banner, Web Advantage section with a
//    feature-by-feature comparison table, hero "web-based" pill.
// 7. Capability list from the client: the Why section (8 cards, `benefits`),
//    the Features section (Input options panel `inputGroups` + 4 cards) and the
//    hero copy are built around the 8 KinetiQ capabilities. All of that text
//    lives in plain arrays/strings so it can be edited in place.
// 6. Contact Us — separate page at /contact (no longer a section here); the
//    footer email / social URLs are TODO-flagged until the team confirms them.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/KineticLandingPage.css';
import CommonFooterRibbon from './Footer';

// Logo — KinetiQ wordmark (background removed). Place this file alongside
// the component and update the path below if your folder structure differs.
import logoSrc from './kinetiq-logo.png';

// ─── HOW TO REGISTER — quick-help modal ────────────────────────────────────────
// Lightweight, self-contained "how do I sign up" reference for people who
// land on the marketing site and haven't created an account yet — they
// won't have reached the in-app Help > User Guide yet, so this can't live
// only inside the authenticated app. Kept intentionally short (3 steps +
// screenshots) rather than reproducing the full Registration section of the
// User Guide; a link at the bottom sends anyone who wants full detail there.
const RegisterHelpModal = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="rh-overlay" onClick={onClose}>
      <div
        className="rh-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rh-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="rh-close" onClick={onClose} aria-label="Close">×</button>

        <div className="rh-header">
          <span className="rh-tag">Quick Help</span>
          <h3 id="rh-title">How to Register</h3>
          <p>Three steps to get your KinetiQ account up and running.</p>
        </div>

        <div className="rh-steps">
          <div className="rh-step">
            <div className="rh-step-num">1</div>
            <div className="rh-step-body">
              <div className="rh-step-title">Open the registration page</div>
              <p>Go to <strong>kinetiqmodel.com/register</strong> and fill in your name, email, and password. Click <strong>Create Account →</strong> </p>
            </div>
          </div>

          <div className="rh-step">
              <div className="rh-shot">
                <img src="/02-register.png" alt="KinetiQ registration page with role selection and Create Account button" loading="lazy" />
                <span className="rh-shot-note">Create Account → button, highlighted</span>
            </div>
          </div>

          {/* <div className="rh-step">
            <div className="rh-step-num">2</div>
            <div className="rh-step-body">
              <div className="rh-step-title">Verify your email &amp; sign in</div>
              <p>Click the verification link we send you, then log in from the homepage. Check Spam/Junk if it doesn't arrive within a few minutes.</p>
            </div>
          </div> */}
        </div>

        <div className="rh-footer">
          <a href="/register" className="rh-cta">Create your account</a>
        </div>
      </div>
    </div>
  );
};

// ─── Scroll-reveal hook ───────────────────────────────────────────────────────
// Adds a `visible` class once an element scrolls into view (used by the CSS
// rules under "SCROLL ANIMATIONS"). Fires once per element, respects
// prefers-reduced-motion by marking everything visible immediately.
const useScrollReveal = (ref) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReduced = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    if (prefersReduced) {
      el.classList.add('visible');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
};

// Wraps any card so it picks up the .visible reveal class on scroll-into-view.
const Reveal = ({ as: Tag = 'div', className = '', children, ...rest }) => {
  const ref = useRef(null);
  useScrollReveal(ref);
  return (
    <Tag ref={ref} className={className} {...rest}>
      {children}
    </Tag>
  );
};

// ─── Animated kinetics curve (hero chart) ─────────────────────────────────────
// Animates once when scrolled into view (IntersectionObserver). A `fired` ref
// blocks re-triggering; the observer disconnects after first reveal.
// ─── ANIMATED HERO VISUAL ─────────────────────────────────────────────────────
// 3-slide carousel that continuously rotates through:
//   Slide 0 — Live kinetics curve  (animated chart with cycling runs)
//   Slide 1 — Chemical reactor plant illustration (SVG)
//   Slide 2 — Flow-pipeline / process diagram (SVG)
// Each slide auto-advances every ~7 s; dots at bottom let users jump.

const RUNS = [
  {
    label: 'Methyl acetate Hydrolysis · Run 8 · 50 °C',
    r2: '0.9983',
    params: [['kf', '3.14×10⁻³ L·mol⁻¹·s⁻¹'], ['Ea', '62.4 kJ/mol'], ['Keq', '4.82']],
    reactant: 'M28,22 C70,28 115,46 160,62 C200,75 250,86 320,92',
    product:  'M28,96 C70,90 115,72 160,55 C200,40 250,28 320,18',
    rDots: [[40,30],[75,42],[110,53],[145,63],[180,72],[215,80],[250,86],[285,90],[320,93]],
    pDots: [[40,92],[75,85],[110,77],[145,69],[180,59],[215,48],[250,36],[285,27],[320,22]],
  },
  {
    label: 'ethyl acetate Saponification · Run 3 · 40 °C',
    r2: '0.9971',
    params: [['kf', '1.87×10⁻³ L·mol⁻¹·s⁻¹'], ['Ea', '58.9 kJ/mol'], ['Keq', '3.61']],
    reactant: 'M28,18 C80,30 140,52 190,67 C230,78 270,87 320,91',
    product:  'M28,95 C80,84 140,65 190,50 C230,38 270,28 320,20',
    rDots: [[40,24],[75,36],[110,48],[145,58],[185,68],[220,76],[258,83],[292,88],[320,91]],
    pDots: [[40,94],[75,87],[110,78],[145,67],[185,55],[220,45],[258,34],[292,26],[320,20]],
  },
  {
    label: 'Acetic Anhydride · Run 5 · 60 °C',
    r2: '0.9994',
    params: [['kf', '4.52×10⁻³ L·mol⁻¹·s⁻¹'], ['Ea', '66.1 kJ/mol'], ['Keq', '5.30']],
    reactant: 'M28,26 C60,34 100,50 150,63 C200,76 260,88 320,93',
    product:  'M28,94 C60,84 100,68 150,54 C200,40 260,26 320,18',
    rDots: [[40,33],[72,44],[108,55],[142,64],[178,73],[212,81],[248,87],[284,91],[320,93]],
    pDots: [[40,93],[72,86],[108,76],[142,66],[178,57],[212,46],[248,34],[284,25],[320,18]],
  },
];

// Floating teal particle bubble
const Particle = ({ style }) => (
  <div className="hero-particle" style={style} aria-hidden="true" />
);

/* ── SLIDE 0: Live Kinetics Chart ─────────────────────────────────────── */
const KineticsCurve = () => {
  const [runIdx, setRunIdx]     = useState(0);
  const [phase, setPhase]       = useState('draw');
  const [dotCount, setDotCount] = useState(0);
  const timerRef = useRef(null);
  const run = RUNS[runIdx];

  useEffect(() => {
    const clear = () => clearTimeout(timerRef.current);
    clear();
    if (phase === 'draw') {
      setDotCount(0);
      let i = 0;
      const drip = () => {
        i++; setDotCount(i);
        if (i < run.rDots.length) timerRef.current = setTimeout(drip, 110);
        else timerRef.current = setTimeout(() => setPhase('hold'), 800);
      };
      timerRef.current = setTimeout(drip, 400);
    }
    if (phase === 'hold')       timerRef.current = setTimeout(() => setPhase('optimising'), 1800);
    if (phase === 'optimising') timerRef.current = setTimeout(() => setPhase('fade'), 1400);
    if (phase === 'fade') {
      timerRef.current = setTimeout(() => {
        setRunIdx(i => (i + 1) % RUNS.length);
        setDotCount(0); setPhase('draw');
      }, 600);
    }
    return clear;
  }, [phase, runIdx, run.rDots.length]);

  const fading = phase === 'fade';

  return (
    <div style={{ width:'100%', flex:1, display:'flex', flexDirection:'column', gap:10, opacity: fading?0:1, transition:'opacity 0.5s ease' }}>
      {/* Header row */}
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.78rem', color:'rgba(255,255,255,0.6)', flexShrink:0 }}>
        <span>{run.label}</span>
        <span style={{ color:'#00C2A8', fontWeight:700 }}>R² = {run.r2}</span>
      </div>
      {/* Chart — flex:1 so it fills remaining space */}
      <div style={{ background:'rgba(255,255,255,0.92)', borderRadius:10, padding:'12px 10px 4px', position:'relative', flex:1, display:'flex', flexDirection:'column' }}>
        {phase === 'optimising' && (
          <div style={{ position:'absolute', inset:0, borderRadius:10, background:'rgba(0,194,168,0.10)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2 }}>
            <span style={{ fontSize:'0.72rem', fontWeight:700, color:'#00C2A8', letterSpacing:'0.08em', textTransform:'uppercase', animation:'hero-blink 0.6s steps(1) infinite' }}>
              ⚙ Optimising parameters…
            </span>
          </div>
        )}
        <svg viewBox="0 0 360 110" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ width:'100%', flex:1, display:'block' }}>
          {[20,40,60,80].map(y => <line key={y} x1="28" y1={y} x2="346" y2={y} stroke="rgba(214,228,236,0.6)" strokeWidth="1"/>)}
          <line x1="28" y1="100" x2="346" y2="100" stroke="#9fb3c4" strokeWidth="1.2"/>
          <line x1="28" y1="10"  x2="28"  y2="100" stroke="#9fb3c4" strokeWidth="1.2"/>
          <text x="187" y="108" fill="#6B7A8D" fontSize="7" fontFamily="Inter" textAnchor="middle">Time (min)</text>
          <path key={`rc-${runIdx}`} d={run.reactant} stroke="#1A3C6E" strokeWidth="2" strokeLinecap="round" className="kinetic-curve drawn"/>
          <path key={`pc-${runIdx}`} d={run.product}  stroke="#00C2A8" strokeWidth="2" strokeLinecap="round" className="kinetic-curve kinetic-curve-product drawn"/>
          {run.rDots.slice(0,dotCount).map(([x,y],i) => <circle key={`r${runIdx}-${i}`} cx={x} cy={y} r="3.2" fill="#fff" stroke="#1A3C6E" strokeWidth="1.8" style={{ animation:'dot-pop 0.25s cubic-bezier(.34,1.6,.64,1) both' }}/>)}
          {run.pDots.slice(0,Math.max(0,dotCount-1)).map(([x,y],i) => <circle key={`p${runIdx}-${i}`} cx={x} cy={y} r="3.2" fill="#fff" stroke="#00C2A8" strokeWidth="1.8" style={{ animation:'dot-pop 0.25s cubic-bezier(.34,1.6,.64,1) both' }}/>)}
          {phase==='draw' && <line x1={28+(dotCount/run.rDots.length)*292} y1="10" x2={28+(dotCount/run.rDots.length)*292} y2="100" stroke="rgba(0,194,168,0.35)" strokeWidth="1" strokeDasharray="3 3"/>}
        </svg>
      </div>
      {/* Parameter pills */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', flexShrink:0 }}>
        {run.params.map(([label,value]) => (
          <span key={label} style={{ background:'rgba(0,194,168,0.12)', border:'1px solid rgba(0,194,168,0.3)', borderRadius:99, padding:'5px 12px', fontSize:'0.78rem', color:'#fff', animation: phase==='optimising'?'hero-blink 0.7s steps(1) infinite':'none' }}>
            <strong style={{ color:'#00C2A8' }}>{label}</strong> = {value}
          </span>
        ))}
      </div>
    </div>
  );
};

/* ── SLIDE 1: Chemical Reactor / Catalytic Plant ─────────────────────── */
const ReactorPlantSlide = () => (
  <div style={{ width:'100%', height:'100%', display:'flex', flexDirection:'column', gap:12 }}>
    <div style={{ fontSize:'0.78rem', color:'rgba(255,255,255,0.6)', display:'flex', justifyContent:'space-between' }}>
      <span>Catalytic Fixed-Bed Reactor · Unit 3</span>
      <span style={{ color:'#00C2A8', fontWeight:700 }}>T = 320 °C · P = 18 bar</span>
    </div>
    <svg viewBox="0 0 420 240" fill="none" xmlns="http://www.w3.org/2000/svg"
      style={{ width:'100%', flex:1, display:'block' }} aria-label="Chemical reactor plant diagram">

      {/* ── Background grid ── */}
      {[40,80,120,160,200].map(y => <line key={y} x1="0" y1={y} x2="420" y2={y} stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>)}
      {[60,120,180,240,300,360].map(x => <line key={x} x1={x} y1="0" x2={x} y2="240" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>)}

      {/* ── Feed pre-heater (left box) ── */}
      <rect x="18" y="70" width="64" height="100" rx="6" fill="#1A3C6E" stroke="rgba(0,194,168,0.5)" strokeWidth="1.5"/>
      <text x="50" y="113" fill="#00C2A8" fontSize="7" fontFamily="Inter" textAnchor="middle" fontWeight="700">PRE</text>
      <text x="50" y="123" fill="#00C2A8" fontSize="7" fontFamily="Inter" textAnchor="middle" fontWeight="700">HEATER</text>
      {/* heat coil squiggles */}
      {[85,95,105,115,125,135].map((y,i) => (
        <path key={y} d={`M26,${y} Q34,${y-5} 42,${y} Q50,${y+5} 58,${y} Q66,${y-5} 74,${y}`}
          stroke="rgba(255,120,60,0.6)" strokeWidth="1.2" fill="none"/>
      ))}
      <text x="50" y="180" fill="rgba(255,255,255,0.4)" fontSize="6" fontFamily="Inter" textAnchor="middle">Feed In</text>

      {/* ── Pipe from pre-heater → reactor ── */}
      <path d="M82,120 L130,120" stroke="#00C2A8" strokeWidth="2.5" strokeLinecap="round"/>
      <circle className="pipe-bubble" cx="106" cy="120" r="3" fill="#00C2A8" opacity="0.7"
        style={{ animation:'pipe-flow 1.8s linear infinite' }}/>

      {/* ── Main reactor vessel ── */}
      <rect x="130" y="30" width="100" height="180" rx="12" fill="#0d2440" stroke="rgba(0,194,168,0.6)" strokeWidth="2"/>
      {/* catalyst bed layers */}
      {[70,95,120,145,170].map((y,i) => (
        <rect key={y} x="138" y={y} width="84" height="14" rx="3"
          fill={i%2===0 ? 'rgba(0,194,168,0.18)' : 'rgba(26,60,110,0.6)'}
          stroke="rgba(0,194,168,0.25)" strokeWidth="0.8"/>
      ))}
      {/* catalyst dots */}
      {[76,82,88,101,107,113,126,132,138,151,157,163,176,182,188].map((x,i) => (
        <circle key={i} cx={x} cy={[77,102,127,152,177][Math.floor(i/3)]+4} r="2.5"
          fill="rgba(0,194,168,0.5)" stroke="rgba(0,194,168,0.8)" strokeWidth="0.5"/>
      ))}
      <text x="180" y="48" fill="#00C2A8" fontSize="8" fontFamily="Inter" textAnchor="middle" fontWeight="700">REACTOR</text>
      <text x="180" y="222" fill="rgba(255,255,255,0.4)" fontSize="6" fontFamily="Inter" textAnchor="middle">Fixed-Bed Catalyst</text>

      {/* ── Temp sensors on reactor ── */}
      {[80,130,180].map((y,i) => (
        <g key={y}>
          <line x1="230" y1={y} x2="248" y2={y} stroke="rgba(255,200,60,0.7)" strokeWidth="1"/>
          <rect x="248" y={y-8} width="28" height="16" rx="3" fill="#1A3C6E" stroke="rgba(255,200,60,0.5)" strokeWidth="1"/>
          <text x="262" y={y+4} fill="#ffc83c" fontSize="6" fontFamily="monospace" textAnchor="middle">{[320,318,316][i]}°C</text>
        </g>
      ))}

      {/* ── Pipe reactor → separator ── */}
      <path d="M230,120 L290,120" stroke="#00C2A8" strokeWidth="2.5" strokeLinecap="round"/>
      <circle cx="260" cy="120" r="3" fill="#00C2A8" opacity="0.7"
        style={{ animation:'pipe-flow 1.8s 0.6s linear infinite' }}/>

      {/* ── Separator / condenser ── */}
      <ellipse cx="310" cy="85" rx="24" ry="34" fill="#0d2440" stroke="rgba(0,194,168,0.5)" strokeWidth="1.5"/>
      <line x1="286" y1="85" x2="334" y2="85" stroke="rgba(0,194,168,0.3)" strokeWidth="0.8" strokeDasharray="3 2"/>
      <text x="310" y="60" fill="#00C2A8" fontSize="6" fontFamily="Inter" textAnchor="middle" fontWeight="700">SEP</text>
      {/* gas outlet top */}
      <path d="M310,51 L310,30" stroke="rgba(120,200,255,0.6)" strokeWidth="2" strokeLinecap="round"/>
      <text x="310" y="26" fill="rgba(120,200,255,0.7)" fontSize="6" fontFamily="Inter" textAnchor="middle">Gas</text>
      {/* liquid outlet bottom */}
      <path d="M310,119 L310,140" stroke="rgba(0,194,168,0.6)" strokeWidth="2" strokeLinecap="round"/>

      {/* ── Heat exchanger (bottom right) ── */}
      <rect x="278" y="140" width="64" height="52" rx="6" fill="#1A3C6E" stroke="rgba(0,194,168,0.4)" strokeWidth="1.5"/>
      {[150,158,166,174,182].map(y => (
        <line key={y} x1="285" y1={y} x2="335" y2={y} stroke="rgba(0,194,168,0.25)" strokeWidth="1"/>
      ))}
      <text x="310" y="175" fill="rgba(255,255,255,0.5)" fontSize="6" fontFamily="Inter" textAnchor="middle">H/E</text>
      {/* outlet pipe */}
      <path d="M342,166 L380,166 L380,200" stroke="#00C2A8" strokeWidth="2" strokeLinecap="round"/>
      <circle cx="380" cy="200" r="5" fill="none" stroke="#00C2A8" strokeWidth="1.5"/>
      <text x="380" y="216" fill="rgba(255,255,255,0.4)" fontSize="6" fontFamily="Inter" textAnchor="middle">Product</text>

      {/* ── Recycle line ── */}
      <path d="M380,195 L400,195 L400,15 L50,15 L50,70"
        stroke="rgba(255,200,60,0.4)" strokeWidth="1.5" strokeDasharray="5 3" strokeLinecap="round"/>
      <text x="225" y="12" fill="rgba(255,200,60,0.6)" fontSize="6" fontFamily="Inter" textAnchor="middle">Recycle</text>

      {/* ── Feed inlet ── */}
      <path d="M0,120 L18,120" stroke="#00C2A8" strokeWidth="2.5" strokeLinecap="round"/>
      <text x="8" y="114" fill="rgba(255,255,255,0.4)" fontSize="6" fontFamily="Inter" textAnchor="middle">Feed</text>

      {/* animated flow circles */}
      {[0,1,2].map(i => (
        <circle key={i} cx="50" cy="15" r="2.5" fill="rgba(255,200,60,0.5)"
          style={{ animation:`pipe-flow 2.5s ${i*0.8}s linear infinite`, offsetPath:'path("M50,15 L400,15 L400,195 L380,195")' }}/>
      ))}
    </svg>
    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
      {[['k_eff','1.84×10⁻² s⁻¹'],['Ea','54.2 kJ/mol'],['R²','0.996']].map(([l,v]) => (
        <span key={l} style={{ background:'rgba(0,194,168,0.12)', border:'1px solid rgba(0,194,168,0.3)', borderRadius:99, padding:'4px 12px', fontSize:'0.78rem', color:'#fff' }}>
          <strong style={{ color:'#00C2A8' }}>{l}</strong> = {v}
        </span>
      ))}
    </div>
  </div>
);

/* ── SLIDE 2: Flow Pipeline / Process Flow Diagram ───────────────────── */
const PipelineSlide = () => (
  <div style={{ width:'100%', height:'100%', display:'flex', flexDirection:'column', gap:12 }}>
    <div style={{ fontSize:'0.78rem', color:'rgba(255,255,255,0.6)', display:'flex', justifyContent:'space-between' }}>
      <span>Process Flow · Methanol Synthesis Loop</span>
      <span style={{ color:'#00C2A8', fontWeight:700 }}>Live Simulation</span>
    </div>
    <svg viewBox="0 0 420 240" fill="none" xmlns="http://www.w3.org/2000/svg"
      style={{ width:'100%', flex:1, display:'block' }} aria-label="Methanol synthesis pipeline process flow">

      {/* BG */}
      <rect width="420" height="240" fill="rgba(10,25,50,0.6)" rx="8"/>

      {/* ── Storage tank (left) ── */}
      <ellipse cx="44" cy="140" rx="28" ry="10" fill="#1A3C6E" stroke="rgba(0,194,168,0.5)" strokeWidth="1.5"/>
      <rect x="16" y="100" width="56" height="40" fill="#1A3C6E" stroke="rgba(0,194,168,0.5)" strokeWidth="1.5"/>
      <ellipse cx="44" cy="100" rx="28" ry="10" fill="#0d2440" stroke="rgba(0,194,168,0.5)" strokeWidth="1.5"/>
      {/* liquid level */}
      <ellipse cx="44" cy="125" rx="26" ry="8" fill="rgba(0,194,168,0.25)"/>
      <text x="44" y="128" fill="rgba(0,194,168,0.9)" fontSize="7" fontFamily="Inter" textAnchor="middle">CO₂/H₂</text>
      <text x="44" y="160" fill="rgba(255,255,255,0.4)" fontSize="6" fontFamily="Inter" textAnchor="middle">Feed Tank</text>

      {/* ── Compressor ── */}
      <circle cx="130" cy="130" r="22" fill="#0d2440" stroke="rgba(0,194,168,0.5)" strokeWidth="1.5"/>
      {/* fan blades */}
      {[0,60,120,180,240,300].map((deg,i) => {
        const r = deg*Math.PI/180;
        return <line key={i} x1="130" y1="130" x2={130+18*Math.cos(r)} y2={130+18*Math.sin(r)}
          stroke="rgba(0,194,168,0.5)" strokeWidth="1.5" strokeLinecap="round"/>;
      })}
      <circle cx="130" cy="130" r="5" fill="#00C2A8"/>
      <text x="130" y="163" fill="rgba(255,255,255,0.4)" fontSize="6" fontFamily="Inter" textAnchor="middle">Compressor</text>

      {/* ── Pipe tank → compressor ── */}
      <path d="M72,125 L108,125" stroke="#00C2A8" strokeWidth="3" strokeLinecap="round"/>
      <circle cx="90" cy="125" r="3.5" fill="#00C2A8" style={{ animation:'pipe-flow 1.4s linear infinite' }}/>

      {/* ── Heat Exchanger (middle-top) ── */}
      <rect x="188" y="70" width="56" height="40" rx="6" fill="#0d2440" stroke="rgba(255,160,50,0.6)" strokeWidth="1.5"/>
      {[80,90,100].map(y => (
        <path key={y} d={`M196,${y} C208,${y-5} 220,${y+5} 232,${y} C244,${y-5} 232,${y}`}
          stroke="rgba(255,120,50,0.5)" strokeWidth="1.2" fill="none"/>
      ))}
      <text x="216" y="64" fill="rgba(255,160,50,0.8)" fontSize="6" fontFamily="Inter" textAnchor="middle">Heat Exch.</text>

      {/* ── Reactor (center) ── */}
      <rect x="188" y="125" width="56" height="80" rx="8" fill="#0d2440" stroke="rgba(0,194,168,0.6)" strokeWidth="2"/>
      {[140,155,170,185].map((y,i) => (
        <rect key={y} x="196" y={y} width="40" height="10" rx="2"
          fill={i%2===0?'rgba(0,194,168,0.2)':'rgba(26,60,110,0.5)'}
          stroke="rgba(0,194,168,0.2)" strokeWidth="0.8"/>
      ))}
      <text x="216" y="143" fill="#00C2A8" fontSize="7" fontFamily="Inter" textAnchor="middle" fontWeight="700">REACTOR</text>
      <text x="216" y="215" fill="rgba(255,255,255,0.4)" fontSize="6" fontFamily="Inter" textAnchor="middle">Cu/ZnO/Al₂O₃</text>

      {/* ── Pipe compressor → HE ── */}
      <path d="M152,115 L152,90 L188,90" stroke="#00C2A8" strokeWidth="2.5" strokeLinecap="round"/>
      <circle cx="152" cy="100" r="3" fill="#00C2A8" style={{ animation:'pipe-flow-v 1.6s linear infinite' }}/>

      {/* ── Pipe HE → reactor ── */}
      <path d="M216,110 L216,125" stroke="rgba(255,160,50,0.8)" strokeWidth="2.5" strokeLinecap="round"/>

      {/* ── Flash drum (right) ── */}
      <rect x="320" y="90" width="48" height="100" rx="8" fill="#0d2440" stroke="rgba(120,180,255,0.5)" strokeWidth="1.5"/>
      {/* liquid level */}
      <rect x="322" y="155" width="44" height="33" rx="5" fill="rgba(0,194,168,0.15)"/>
      <text x="344" y="118" fill="rgba(120,180,255,0.9)" fontSize="7" fontFamily="Inter" textAnchor="middle">FLASH</text>
      <text x="344" y="128" fill="rgba(120,180,255,0.9)" fontSize="7" fontFamily="Inter" textAnchor="middle">DRUM</text>
      <text x="344" y="172" fill="rgba(0,194,168,0.7)" fontSize="6" fontFamily="Inter" textAnchor="middle">MeOH liq.</text>
      {/* gas out top */}
      <path d="M344,90 L344,65 L390,65" stroke="rgba(120,180,255,0.5)" strokeWidth="2" strokeLinecap="round"/>
      <text x="390" y="60" fill="rgba(120,180,255,0.6)" fontSize="6" fontFamily="Inter" textAnchor="middle">Gas recycle</text>
      {/* liquid out bottom */}
      <path d="M344,190 L344,220 L400,220" stroke="#00C2A8" strokeWidth="2.5" strokeLinecap="round"/>
      <circle cx="400" cy="220" r="6" fill="none" stroke="#00C2A8" strokeWidth="1.5"/>
      <text x="400" y="235" fill="rgba(255,255,255,0.5)" fontSize="6" fontFamily="Inter" textAnchor="middle">MeOH Out</text>

      {/* ── Pipe reactor → flash ── */}
      <path d="M244,160 L320,160" stroke="#00C2A8" strokeWidth="2.5" strokeLinecap="round"/>
      <circle cx="270" cy="160" r="3" fill="#00C2A8" style={{ animation:'pipe-flow 1.5s 0.3s linear infinite' }}/>
      <circle cx="296" cy="160" r="3" fill="#00C2A8" style={{ animation:'pipe-flow 1.5s 0.8s linear infinite' }}/>

      {/* ── Recycle arrow (top) ── */}
      <path d="M390,65 L410,65 L410,30 L130,30 L130,108"
        stroke="rgba(120,180,255,0.4)" strokeWidth="1.5" strokeDasharray="5 3" strokeLinecap="round"/>
      <polygon points="126,108 134,108 130,116" fill="rgba(120,180,255,0.6)"/>

      {/* ── Live data labels ── */}
      <rect x="155" y="48" width="46" height="14" rx="3" fill="rgba(0,194,168,0.15)" stroke="rgba(0,194,168,0.3)" strokeWidth="0.8"/>
      <text x="178" y="58" fill="#00C2A8" fontSize="6.5" fontFamily="monospace" textAnchor="middle">250 °C</text>
      <rect x="260" y="148" width="50" height="14" rx="3" fill="rgba(0,194,168,0.15)" stroke="rgba(0,194,168,0.3)" strokeWidth="0.8"/>
      <text x="285" y="158" fill="#00C2A8" fontSize="6.5" fontFamily="monospace" textAnchor="middle">78 bar</text>
    </svg>
    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
      {[['T_rxr','250 °C'],['P_sys','78 bar'],['k_rxn','2.4×10⁻³ s⁻¹']].map(([l,v]) => (
        <span key={l} style={{ background:'rgba(0,194,168,0.12)', border:'1px solid rgba(0,194,168,0.3)', borderRadius:99, padding:'4px 12px', fontSize:'0.78rem', color:'#fff' }}>
          <strong style={{ color:'#00C2A8' }}>{l}</strong> = {v}
        </span>
      ))}
    </div>
  </div>
);

/* ── CAROUSEL wrapper ─────────────────────────────────────────────────── */
const SLIDES = [
  { id: 0, label: 'Kinetics Chart',   Component: KineticsCurve    },
  { id: 1, label: 'Reactor Plant',    Component: ReactorPlantSlide },
  { id: 2, label: 'Process Pipeline', Component: PipelineSlide     },
];
const SLIDE_DURATION = 7000; // ms per slide

const HeroCarousel = () => {
  const [active, setActive]   = useState(0);
  const [visible, setVisible] = useState(0); // what's actually rendered (lags by fade)
  const intervalRef = useRef(null);

  const goTo = (idx) => {
    setActive(idx);
    setTimeout(() => setVisible(idx), 420);
  };

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setActive(prev => {
        const next = (prev + 1) % SLIDES.length;
        setTimeout(() => setVisible(next), 420);
        return next;
      });
    }, SLIDE_DURATION);
    return () => clearInterval(intervalRef.current);
  }, []);

  const SlideComp = SLIDES[visible].Component;

  return (
    <div className="hero-carousel">
      {/* Floating teal particles — sit behind everything */}
      {[
        { width:6, height:6, top:'10%', left:'6%',  '--delay':'0s',   '--dur':'7s'   },
        { width:4, height:4, top:'65%', left:'12%', '--delay':'1.5s', '--dur':'9s'   },
        { width:8, height:8, top:'35%', left:'90%', '--delay':'0.8s', '--dur':'6s'   },
        { width:5, height:5, top:'78%', left:'80%', '--delay':'2.2s', '--dur':'8s'   },
        { width:3, height:3, top:'18%', left:'55%', '--delay':'3s',   '--dur':'10s'  },
        { width:7, height:7, top:'50%', left:'42%', '--delay':'0.3s', '--dur':'7.5s' },
      ].map((s,i) => <Particle key={i} style={s}/>)}

      {/* Slide content */}
      <div
        key={visible}
        style={{
          width:'100%', position:'relative', zIndex:1,
          opacity: active === visible ? 1 : 0,
          transition: 'opacity 0.4s ease',
        }}
      >
        <SlideComp />
      </div>

      {/* Dot navigation */}
      <div style={{ display:'flex', gap:6, justifyContent:'center', marginTop:14, position:'relative', zIndex:2 }}>
        {SLIDES.map((_,i) => (
          <button
            key={i}
            onClick={() => { clearInterval(intervalRef.current); goTo(i); }}
            aria-label={`Slide ${i+1}`}
            style={{
              width: i===active ? 20 : 6, height:6,
              borderRadius:99, border:'none', cursor:'pointer', padding:0,
              background: i===active ? '#00C2A8' : 'rgba(255,255,255,0.25)',
              transition:'all 0.4s ease',
            }}
          />
        ))}
      </div>
    </div>
  );
};

// ─── NAV ──────────────────────────────────────────────────────────────────────
// Receives scrollContainerRef so it tracks the container's scrollTop,
// not window.scrollY (which is always 0 when html/body overflow:hidden).
// Which nav tab owns each on-page section. The "web advantage" block sits between
// Why KinetiQ and Features, so it keeps the Why KinetiQ tab highlighted.
const NAV_SECTIONS = [
  ['why', 'why'],
  ['web-advantage', 'why'],
  ['features', 'features'],
  ['demo', 'demo'],
  ['how-it-works', 'how-it-works'],
];

// `currentPageId` is only passed by other pages that reuse this navbar (the About Us
// page passes 'about-us'): that tab is highlighted and scroll-spy is switched off.
const Nav = ({ scrollContainerRef, onNavClick, onOpenRegisterHelp, currentPageId = '' }) => {
  const [scrolled, setScrolled] = useState(false);
  // Tab for the section currently in view ('' while on the hero, above the first section).
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    const el = scrollContainerRef?.current;
    if (!el) return;
    const fn = () => {
      setScrolled(el.scrollTop > 10);

      // Scroll-spy: the active section is the last one whose top has passed just
      // under the sticky navbar.
      const containerTop = el.getBoundingClientRect().top;
      const line = containerTop + 120;
      let current = '';
      NAV_SECTIONS.forEach(([sectionId, tabId]) => {
        const node = document.getElementById(sectionId);
        if (node && node.getBoundingClientRect().top <= line) current = tabId;
      });
      setActiveId(currentPageId || current);
    };
    fn();
    el.addEventListener('scroll', fn, { passive: true });
    window.addEventListener('resize', fn);
    return () => {
      el.removeEventListener('scroll', fn);
      window.removeEventListener('resize', fn);
    };
  }, [scrollContainerRef, currentPageId]);

  return (
    <nav className="nav" style={scrolled ? { boxShadow: '0 4px 18px rgba(26,60,110,0.10)' } : undefined}>
      <div className="nav-inner">

        <a
          href="/"
          className="nav-logo"
          onClick={(e) => { e.preventDefault(); onNavClick('top'); }}
        >
          {/* Full wordmark logo (icon + "KinetiQ Reaction Modeling") with
              background removed — see KineticLandingPage.css .nav-logo img
              for sizing. No separate text label here: the wordmark is
              already baked into the image, so adding "KinetiQ" again would
              duplicate the brand name. */}
          <img
            src={logoSrc}
            alt="KinetiQ — Reaction Modeling"
            className="nav-logo-img"
          />
        </a>

        <div className="nav-links">
          {[
            { label: 'Why KinetiQ', id: 'why' },
            { label: 'Features', id: 'features' },
            { label: 'Demo', id: 'demo' },
            { label: 'How it works', id: 'how-it-works' },
            { label: 'About Us', id: 'about-us' },   // opens the /about-us page (see handleNavClick)
            { label: 'Contact Us', id: 'contact' },
          ].map(({ label, id }) => (
            <a
              key={id}
              href={`#${id}`}
              className={activeId === id ? 'nav-link-active' : undefined}
              aria-current={activeId === id ? 'true' : undefined}
              onClick={(e) => { e.preventDefault(); onNavClick(id); }}
            >
              {label}
            </a>
          ))}

          {/* How to Register — separate from the scroll-links above: opens
              a short quick-help modal instead of jumping to a page section,
              so it stays visible/usable from anywhere on the site. */}
          <a
            href="/how-to-register"
            className="nav-help-link"
            onClick={(e) => { e.preventDefault(); onOpenRegisterHelp(); }}
          >
            <span className="nav-help-icon" aria-hidden="true">?</span>
            <span className="nav-help-text">How to Register</span>
          </a>
        </div>

        <a
          href="/login"
          className="nav-cta"
          onClick={(e) => { e.preventDefault(); onNavClick('get-started'); }}
        >
          Login / Register
        </a>

      </div>
    </nav>
  );
};

// ─── HERO ─────────────────────────────────────────────────────────────────────
const Hero = ({ onNavClick }) => (
  <section className="hero">
    <div className="container hero-inner">

      {/* Left: copy */}
      <div>
        <span className="hero-badge">KinetiQ &middot; Browser-based kinetic regression</span>

        <h1>
          Kinetic regression in your browser,{' '}
          <em>without writing reactor-model code.</em>
        </h1>

        <p className="hero-description">
          Upload your experimental data, describe your reactions, and KinetiQ
          fits the rate parameters for you. Batch or continuous, one data set
          or many, one reaction or several: a Bayesian optimizer does the
          searching, and you check the fit on screen. Nothing to install.
        </p>

        <div className="hero-actions">
          <a
            href="/login"
            className="btn-primary"
            onClick={(e) => { e.preventDefault(); onNavClick('get-started'); }}
          >
            Start fitting for free
          </a>
          <a
            href="#how-it-works"
            className="btn-secondary"
            onClick={(e) => { e.preventDefault(); onNavClick('how-it-works'); }}
          >
            See how it works
          </a>
        </div>

        <div className="hero-web-pill">
          <span className="pill-icon">🌐</span>
          Runs in any modern browser-nothing to download or install
        </div>
      </div>

      {/* Right: visual — 3-slide carousel (kinetics chart / reactor plant / pipeline) */}
      <div className="hero-visual">
        <div className="hero-carousel-shell">
          <HeroCarousel />
        </div>
      </div>

    </div>
  </section>
);

// ─── DIFFERENTIATOR BANNER ─────────────────────────────────────────────────────
// Thin strip directly under the hero — the first, fastest read of the core
// differentiator Sagar called out: web-based vs. download-and-install tools.
const DifferentiatorBanner = () => (
  <div className="differentiator-banner">
    <div className="container differentiator-banner-inner">
      <strong>100% web-based.</strong>
      <span>No installation</span>
      <span className="diff-divider" />
      <span>No OS compatibility checks</span>
      <span className="diff-divider" />
      <span>No license files</span>
      <span className="diff-divider" />
      <span>Just open a link and start fitting</span>
    </div>
  </div>
);

// ─── WHY KINETIC / VALUE PROPOSITION ────────────────────────────────────────────
// Content for the capabilities grid. Edit the strings below to change the
// text on the page; keep each item to a title and one or two sentences.
// The 8 items map one-to-one to the KinetiQ capability list supplied by the client.
const benefits = [
  {
    icon: '\u{1F310}',
    title: 'Runs in your browser',
    body: 'Open KinetiQ on any computer with a modern browser. There is nothing to download, install or license.',
  },
  {
    icon: '\u26A1',
    title: 'Fast regression, no reactor code',
    body: 'Enter your reactions and data and get fitted kinetic parameters. KinetiQ builds the reactor model, so you do not write or debug any code.',
  },
  {
    icon: '\u{1F9EA}',
    title: 'Inputs that match your experiments',
    body: 'Batch and continuous operation, your own temperatures, pressures, catalyst amounts and volumes, several data sets at once, and several reactions in one fit.',
  },
  {
    icon: '\u{1F3AF}',
    title: 'Bayesian optimizer',
    body: 'A Bayesian optimizer searches the parameter space for the best fit, so you spend less time hunting for good starting guesses.',
  },
  {
    icon: '\u{1F4C8}',
    title: 'See how well the model fits',
    body: 'Plot the model against your experimental data, and compare the results of different models side by side.',
  },
  {
    icon: '\u{1F4DA}',
    title: 'Examples from the literature',
    body: 'Start from worked examples built on published experimental studies, and use them as a reference for your own system.',
  },
  {
    icon: '\u2728',
    title: 'Modern, easy-to-use interface',
    body: 'A clean workflow takes you from data upload to fitted parameters, with no manual to read first.',
  },
  {
    icon: '\u{1F517}',
    title: 'Parameters ready for reactor design',
    body: 'Take the fitted parameters straight into other programs for reactor design. No retyping, no unit juggling.',
  },
];

const WhyKinetic = () => (
  <section className="why-section" id="why">
    <div className="container">
      <div className="why-header">
        <span className="section-label">Why KinetiQ</span>
        <h2 className="section-title">From experimental data to kinetic parameters, without the setup</h2>
        <p className="section-subtitle">
          KinetiQ is built for researchers and process engineers who want
          reliable rate parameters quickly, without installing software or
          writing a reactor model.
        </p>
      </div>

      <div className="benefits-grid benefits-grid-8">
        {benefits.map(({ icon, title, body }) => (
          <Reveal key={title} className="benefit-card">
            <div className="benefit-icon">{icon}</div>
            <h3>{title}</h3>
            <p>{body}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// ─── WEB ADVANTAGE / COMPARISON ─────────────────────────────────────────────────
// Direct callout of the differentiator vs. traditional downloadable kinetics
// software (the category the team's reference tool belongs to).
const WebAdvantage = () => (
  <section className="web-advantage" id="web-advantage">
    <div className="container web-advantage-inner">

      <div className="web-advantage-content">
        <h2>
          Most kinetics software still makes you <em>install something first.</em>
        </h2>
        <p>
          Traditional data-fitting tools are powerful, but they're downloadable
          desktop applications-tied to a specific operating system,
          a license file, and whatever machine you installed them on. KinetiQ
          gives you the same rigorous fitting, with none of that friction.
        </p>
        <ul className="comparison-list">
          <li><span className="check">✓</span> Open any project from any device with a browser</li>
          <li><span className="check">✓</span> Share a project by sending a link, not a file</li>
          <li><span className="check">✓</span> Updates ship automatically -no re-downloading a new version</li>
          <li><span className="check">✓</span> Nothing for IT to approve or whitelist</li>
        </ul>
      </div>

      <div className="comparison-table">
        <div className="comparison-table-header">
          <span>Capability</span>
          <span>KinetiQ</span>
          <span>Desktop tools</span>
        </div>
        {[
          ['Works on Windows, macOS, Linux', true, false],
          ['No installation required', true, false],
          ['Share results via link', true, false],
          ['Rigorous ODE-based fitting', true, true],
          ['Global multi-temperature fitting', true, true],
          ['Always on the latest version', true, false],
        ].map(([label, kinetic, desktop]) => (
          <div className="comparison-table-row" key={label}>
            <span>{label}</span>
            <span className={kinetic ? 'ct-yes' : 'ct-no'}>{kinetic ? '✓' : '—'}</span>
            <span className={desktop ? 'ct-yes' : 'ct-no'}>{desktop ? '✓' : '—'}</span>
          </div>
        ))}
      </div>

    </div>
  </section>
);

// ─── FEATURES ─────────────────────────────────────────────────────────────────
// Content for the "Input options" panel (capability 3 on the client list).
// Edit the group titles or chips below to change what shows on the page.
const inputGroups = [
  {
    title: 'Experiment mode',
    note: 'Fit the way you ran the experiment.',
    chips: ['Batch', 'Continuous operation'],
  },
  {
    title: 'Reaction conditions',
    note: 'Enter the conditions of each run.',
    chips: ['Temperature', 'Pressure', 'Catalyst amount', 'Reaction volume'],
  },
  {
    title: 'Multiple data sets',
    note: 'Fit runs from different experiments together, and see each one on the plots.',
    chips: ['Upload several files', 'Remove a wrong file', 'One set of parameters'],
  },
  {
    title: 'Multiple reactions',
    note: 'Describe a network of reactions instead of one step.',
    chips: ['Reversible and irreversible', 'Exponents per reactant', 'Several reactions in one fit'],
  },
];

const Features = () => (
  <section className="features-section" id="features">
    <div className="container">
      <div className="features-header">
        <span className="section-label">Capabilities</span>
        <h2 className="section-title">What you can do with KinetiQ</h2>
      </div>

      <Reveal className="inputs-panel">
        <div className="inputs-panel-head">
          <h3>Input options that cover your experiments</h3>
          <p>
            Set up the model once, whatever your experiment looks like: the
            operating mode, the conditions, how many data sets you have and
            how many reactions you are fitting.
          </p>
        </div>
        <div className="inputs-grid">
          {inputGroups.map(({ title, note, chips }) => (
            <div className="inputs-group" key={title}>
              <h4>{title}</h4>
              <p>{note}</p>
              <ul className="inputs-chips">
                {chips.map((chip) => <li key={chip}>{chip}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </Reveal>

      <div className="features-grid">

        <Reveal className="feature-card">
          <span className="feature-tag">Optimization</span>
          <h3>Bayesian optimizer for the best fit</h3>
          <p>
            KinetiQ uses a Bayesian optimizer to search for the parameter
            values that match your data best, so a rough first guess is enough
            to get started.
          </p>
        </Reveal>

        <Reveal className="feature-card">
          <span className="feature-tag">Model assessment</span>
          <h3>Check the fit by eye, and compare models</h3>
          <p>
            Plot the fitted model against your experimental data, then run the
            same data with a different model and compare the results.
          </p>
        </Reveal>

        <Reveal className="feature-card">
          <span className="feature-tag">Examples</span>
          <h3>Learn from published studies</h3>
          <p>
            Open examples built from experimental studies in the literature
            and see how the data, model and fitted parameters fit together.
          </p>
        </Reveal>

        <Reveal className="feature-card">
          <span className="feature-tag">Reactor design</span>
          <h3>Use your parameters in other programs</h3>
          <p>
            Fitted parameters can go directly into other tools for reactor
            design, so the result of your regression does not stop at a report.
          </p>
        </Reveal>

      </div>
    </div>
  </section>
);

// ─── DEMO / VIDEO SECTION ────────────────────────────────────────────────────
// Addresses the team's agreement to have a short demo/training video on
// YouTube, plus a LinkedIn page link. Swap YOUTUBE_EMBED_ID once the video
// is live; until then this renders a clearly-labelled placeholder thumbnail
// instead of a broken player.
const YOUTUBE_EMBED_ID = ''; // TODO: paste the real YouTube video ID once published
const LINKEDIN_URL = '#'; // TODO: replace with the real LinkedIn page URL
const YOUTUBE_CHANNEL_URL = '#'; // TODO: replace with the real YouTube channel URL

const Demo = () => {
  const [playing, setPlaying] = useState(false);

  return (
    <section className="demo-section" id="demo">
      <div className="container">
        <div className="demo-section-header">
          <span className="section-label">See it in action</span>
          <h2 className="section-title">A two-minute tour of KinetiQ</h2>
          <p className="section-subtitle" style={{ margin: '0 auto' }}>
            Watch a short walkthrough of uploading data, building a reaction
            network, and exporting fitted parameters or follow us for
            future tutorials and product updates.
          </p>
        </div>

        <div className="demo-grid">

          <div className="demo-video-card">
            <div className="demo-video-thumb" onClick={() => YOUTUBE_EMBED_ID && setPlaying(true)}>
              {playing && YOUTUBE_EMBED_ID ? (
                <iframe
                  title="KinetiQ product demo"
                  src={`https://www.youtube.com/embed/${YOUTUBE_EMBED_ID}?autoplay=1`}
                  style={{ width: '100%', height: '100%', border: 0 }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <button className="play-btn" aria-label="Play demo video">▶</button>
              )}
            </div>
            <div className="demo-video-info">
              <h3>KinetiQ in two minutes</h3>
              <p>From raw CSV to fitted rate constants -the full workflow, start to finish.</p>
              <a href="https://www.youtube.com/watch?v=VwwiAbrrCDI" target="_blank" rel="noopener noreferrer">
                Watch on YouTube →
              </a>
            </div>
          </div>

          <div className="demo-cta-card">
            <h3>Want the full training series?</h3>
            <p>
              Subscribe on YouTube for upcoming tutorials on reaction network
              modelling, global fitting, and exporting results and
              follow our LinkedIn page for product updates.
            </p>
            <div className="social-links-row">
              <a href={YOUTUBE_CHANNEL_URL} target="_blank" rel="noopener noreferrer" className="social-link-btn youtube">
                ▶ YouTube
              </a>
              <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="social-link-btn linkedin">
                in LinkedIn
              </a>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

// ─── TRUSTED BY (logo placeholders) ─────────────────────────────────────────────
// TODO: replace with real institution/customer logos once approved.
// Until then, these are intentionally generic chemistry/research-themed
// icon+wordmark combos (not real organization names or marks) so the section
// doesn't imply an endorsement that hasn't been confirmed yet.
const placeholderLogos = [
  {
    name: 'Catalyst Research Lab',
    href: 'https://www.catalysis.org',
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M9 2v6.5L4.5 17a2 2 0 0 0 1.8 3h11.4a2 2 0 0 0 1.8-3L15 8.5V2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7 2h10" strokeLinecap="round" />
        <path d="M7.5 14h9" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    name: 'Reaction Dynamics Group',
    href: 'https://www.reactiondynamics.org',
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="6" cy="6" r="2.4" />
        <circle cx="18" cy="6" r="2.4" />
        <circle cx="12" cy="17" r="2.4" />
        <path d="M8.1 7.3 10 15.2M15.9 7.3 14 15.2M8.4 6h7.2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    name: 'Institute of Process Chemistry',
    href: 'https://www.processchemistry.eu',
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M3 21h18" strokeLinecap="round" />
        <path d="M5 21V9l7-5 7 5v12" strokeLinejoin="round" />
        <path d="M9 21v-6h6v6" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    name: 'Applied Surface Science Center',
    href: 'https://www.surfacescience.org',
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M3 17c2-3 4-4 6-4s4 2 6 2 4-2 6-2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3 12c2-3 4-4 6-4s4 2 6 2 4-2 6-2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    name: 'National Kinetics Consortium',
    href: 'https://www.kineticsresearch.org',
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M12 3v18M3 12h18" strokeLinecap="round" />
        <circle cx="12" cy="12" r="9" />
      </svg>
    ),
  },
  {
    name: 'Process Engineering Labs',
    href: 'https://www.processengineeringlabs.com',
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M4 21V11l4-3 4 3v10M12 21V8l4-3 4 3v13" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

const TrustedBy = () => (
  <div className="trusted-section">
    <div className="container">
      <div className="trusted-header">Built on KinetiQ, used in research and industry</div>
      <div className="logos-row">
        {placeholderLogos.map(({ name, icon, href }) => (
          <a
            className="logo-placeholder"
            key={name}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${name} (opens in new tab)`}
          >
            <span className="logo-placeholder-icon">{icon}</span>
            <span className="logo-placeholder-name">{name}</span>
          </a>
        ))}
      </div>
    </div>
  </div>
);

// ─── HOW IT WORKS ─────────────────────────────────────────────────────────────
const steps = [
  ['1', 'Add your data and reactions', 'Upload a CSV for each data set, or type values in. Enter your reactions, the operating mode and the conditions of each run.'],
  ['2', 'Choose a model', 'Select the rate law and whether each reaction is reversible. Set your own exponents, or leave the defaults.'],
  ['3', 'Run the fit and review it', 'The Bayesian optimizer finds the parameters. Check the model against your data, compare models, then take the parameters into your reactor design.'],
];

const HowItWorks = () => (
  <section className="how-section" id="how-it-works">
    <div className="container">
      <div className="how-section-header">
        <span className="section-label">Workflow</span>
        <h2 className="section-title">From raw data to kinetic parameters in minutes</h2>
      </div>

      <div className="steps-row">
        {steps.map(([num, title, body]) => (
          <Reveal key={num} className="step-card">
            <div className="step-number">{num}</div>
            <h3>{title}</h3>
            <p>{body}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// ─── CONTACT DETAILS ──────────────────────────────────────────────────────────
// The Contact Us section was removed from this page: "Contact Us" now opens the
// separate /contact page (see handleNavClick). The email below is still used by
// the footer. Replace the placeholder when the production value is supplied.
const CONTACT_EMAIL = 'name@example.com';

// ─── FOOTER ───────────────────────────────────────────────────────────────────
// 4-column layout (KX2 Development style):
//   Col 1 – logo + brand name + tagline
//   Col 2 – Quick Links (page nav)
//   Col 3 – Contact details (address · phone · email)
//   Col 4 – Social icon circles
//   (The green copyright ribbon is the separate common component in Footer.jsx,
//    rendered after this footer in the page root.)
// All styles in KineticLandingPage.css under "── FOOTER ──"

const Footer = ({ onNavClick }) => (
  <footer className="footer">

    {/* ── 4-column body ── */}
    <div className="container">
      <div className="footer-inner">

        {/* COL 1 — Brand */}
        <div className="footer-brand">
          <img
            src={logoSrc}
            alt="KinetiQ — Reaction Modeling"
            className="footer-brand-logo"
          />
          <p className="footer-tagline">
            Web-based kinetic parameter&nbsp;estimation,
            powered by KinetiQ.
          </p>
        </div>

        {/* COL 2 — Quick Links */}
        <div>
          <div className="footer-col-title">Quick Links</div>
          <ul className="footer-nav-list">
            {[
              ['Why KinetiQ',  'why'],
              ['Features',     'features'],
              ['Demo',         'demo'],
              ['How it works', 'how-it-works'],
              ['About Us',     'about-us'],
              ['Contact Us',      'contact'],
              ['Launch App',   'get-started'],
            ].map(([label, id]) => (
              <li key={id}>
                <a
                  href={`#${id}`}
                  onClick={(e) => { e.preventDefault(); onNavClick(id); }}
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* COL 3 — Contact */}
        <div>
          <div className="footer-col-title">Contact Us</div>
          <ul className="footer-contact-list">
            <li className="footer-contact-item">
              <span className="fci-label">Email:</span>
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </li>
          </ul>
        </div>

        {/* COL 4 — Social icons */}
        <div className="footer-social-col">
          <div className="footer-col-title">Follow Us</div>
          <div className="footer-social-icons">
            {/* LinkedIn */}
            <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer"
               className="footer-social-icon" aria-label="LinkedIn">
              <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor">
                <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.37V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.59 0 4.26 2.36 4.26 5.44v6.3zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zm1.78 13.02H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"/>
              </svg>
            </a>
            {/* YouTube */}
            <a href={YOUTUBE_CHANNEL_URL} target="_blank" rel="noopener noreferrer"
               className="footer-social-icon" aria-label="YouTube">
              <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor">
                <path d="M23.5 6.2a3.01 3.01 0 0 0-2.12-2.13C19.54 3.6 12 3.6 12 3.6s-7.54 0-9.38.47A3.01 3.01 0 0 0 .5 6.2C0 8.05 0 12 0 12s0 3.95.5 5.8a3.01 3.01 0 0 0 2.12 2.13C4.46 20.4 12 20.4 12 20.4s7.54 0 9.38-.47a3.01 3.01 0 0 0 2.12-2.13C24 15.95 24 12 24 12s0-3.95-.5-5.8zM9.75 15.52V8.48L15.86 12l-6.11 3.52z"/>
              </svg>
            </a>
          </div>
        </div>

      </div>
    </div>

  </footer>
);

// ─── PAGE ROOT ────────────────────────────────────────────────────────────────
//
// SCROLL FIX EXPLANATION
// ─────────────────────────────────────────────────────────────────────────────
// The app-wide CSS (DataFittingPage.css) sets:
//   html, body { overflow: hidden }
//
// This is needed for the data-fitting dashboard (fixed panels, no page
// scroll). But it prevents the landing page from scrolling via the document.
//
// Solution: .kinetic-landing is its own scroll container.
//   CSS:  height: 100vh; overflow-y: auto
//   JSX:  scrollContainerRef is passed to Nav for scrollTop tracking.
//         onNavClick() smooth-scrolls within the container.
//
// This is self-contained — no changes to router, MainLayout, or any parent.
// ─────────────────────────────────────────────────────────────────────────────
const KineticLandingPage = () => {
  const scrollRef = useRef(null);
  const [registerHelpOpen, setRegisterHelpOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavClick = useCallback((id) => {
    const container = scrollRef.current;
    if (!container) return;

    if (id === 'top') {
      container.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (id === 'get-started') {
      window.location.href = '/login';
      return;
    }

    // About Us is a separate page (route /about-us), not a section of this one.
    if (id === 'about-us') {
      navigate('/about-us');
      return;
    }

    // Contact Us is a separate page (route /contact), not a section of this one.
    if (id === 'contact') {
      navigate('/contact');
      return;
    }

    const target = document.getElementById(id);
    if (target) {
      const offset = target.getBoundingClientRect().top
        - container.getBoundingClientRect().top
        + container.scrollTop
        - 72;
      container.scrollTo({ top: offset, behavior: 'smooth' });
    }
  }, [navigate]);

  // Arriving from another page (e.g. the About Us navbar): that page navigates to '/'
  // with { state: { scrollTo: '<section id>' } }; scroll there once the page is drawn.
  useEffect(() => {
    const target = location.state?.scrollTo;
    if (!target) return undefined;
    const t = setTimeout(() => {
      // 'contact' / 'about-us' are separate pages: handleNavClick navigates away,
      // so don't navigate back to '/' afterwards (that cancelled the move and made
      // a second click necessary).
      handleNavClick(target);
      if (target !== 'contact' && target !== 'about-us') {
        navigate('/', { replace: true, state: null }); // so a refresh doesn't re-scroll
      }
    }, 120);
    return () => clearTimeout(t);
  }, [location.state, handleNavClick, navigate]);

  return (
    <div className="kinetic-landing" ref={scrollRef} style={{ height: '100vh', overflowY: 'auto' }}>
      <Nav
        scrollContainerRef={scrollRef}
        onNavClick={handleNavClick}
        onOpenRegisterHelp={() => setRegisterHelpOpen(true)}
      />
      <Hero onNavClick={handleNavClick} />
      <DifferentiatorBanner />
      <WhyKinetic />
      <WebAdvantage />
      <Features />
      <Demo />
      <TrustedBy />
      <HowItWorks />
      <Footer onNavClick={handleNavClick} />
      <CommonFooterRibbon />

      <RegisterHelpModal
        open={registerHelpOpen}
        onClose={() => setRegisterHelpOpen(false)}
      />
    </div>
  );
};

export { Nav, RegisterHelpModal };
export default KineticLandingPage;